/**
 * Die **Werkzeugleiste**: das Glasband unmittelbar unter der Kopfleiste, in dem
 * die Aktionen eines Screens stehen.
 *
 * Sie gehört zum Rahmen (ADR-0025) und wird nachgezeichnet, nicht komponiert.
 * Vorlage ist die Funktion `Toolbar` in `design/reference/AppShell.jsx` (Zeile
 * 188 ff.), beschrieben in `FRAME.md` unter „Werkzeugleiste"; **ein Baustein des
 * Skills ist sie nicht** — sie steht dort als Referenzcode, an dem abgelesen
 * wird, und deshalb hier neben dem Gerüst aus #40.
 *
 * **Sie sitzt innerhalb der Inhaltsfläche und wiederholt deren Rundung.** Der
 * Referenz-Zusammenbau rundet zweimal: der Inhaltscontainer
 * (`reference/index.html` Zeile 73, mit `overflow: hidden`) und die Leiste darin
 * (`reference/AppShell.jsx` Zeile 189). Das ist gewollt — die Rundung der
 * Inhaltsfläche ist nicht wegzunehmen mit dem Argument, die Leiste bringe sie
 * mit. Aus Glas ist sie, weil sie über dem scrollenden Inhalt schwebt und die
 * ambiente Aura hinter sich hat; ohne die Aura wäre das Glas sinnlos.
 *
 * **Rechts endet jede Werkzeugleiste gleich: mit dem Seitennavigator, wo es
 * etwas zu blättern gibt, davor ein Trenner — sonst leer.** Die Icon-Buttons
 * „Einstellungen" und „Hilfe", mit denen die Vorlage abschließt, fallen
 * ersatzlos: Es gibt hier nichts dahinter, so wie „Einstellungen ▸" auch aus dem
 * Benutzermenü fällt. Ausgeschrieben steht die Weglassung in `design/VENDOR.md`.
 *
 * Das ist der Grund, warum der rechte Rand **kein Platz für Kinder** ist, sondern
 * ein `paging`: Ein Platz, in den der Aufrufer schreiben dürfte, wäre der Ort, an
 * dem die beiden Knöpfe wiederkämen — und niemand sähe es.
 *
 * **Ein Screen mit Aktionen hat eine Werkzeugleiste; ein Screen ohne Aktionen hat
 * keine.** Ein leerer Glasstreifen ist schlechter als keiner. Deshalb zeichnet
 * `ToolbarRegion` von sich aus nichts: Das Band entsteht erst, wo eine Ansicht
 * eine `Toolbar` schreibt.
 *
 * **Warum ein Portal.** Die Leiste ist das erste Flex-Kind der Inhaltsfläche und
 * steht damit **außerhalb** der scrollenden Fläche — sonst scrollte sie mit. Ihr
 * Inhalt gehört aber der Ansicht, samt deren Zustand: Welcher Abschnitt der
 * Verwaltung offen ist, weiß die Verwaltung und nicht die Hülle. Beides zugleich
 * geht nur so: Die Ansicht schreibt die Leiste, wo es ihr passt, und sie
 * erscheint, wo sie hingehört.
 */

import type { ReactElement, ReactNode } from "react";

import { createContext, useContext, useState } from "react";
import { createPortal } from "react-dom";

import { Pagination } from "../design-system";

/**
 * Der Zustand des Blätterns, wie der rechte Rand ihn braucht: die Seite, die
 * Zahl der Seiten, der Bereichstext und der eine Weg, eine Seite zu wählen.
 *
 * Der Bereichstext kommt fertig herein und wird hier nicht gerechnet: Wie „1–25
 * von 57" geschrieben wird, entscheidet `views/list.ts` für alle Listen an einer
 * Stelle (`rangeOf`).
 */
export interface ToolbarPaging {
  page: number;
  pages: number;
  /** „1–25 von 57" — die Schreibweise des Design-Systems für einen Seitenbereich. */
  rangeLabel: string;
  onTurn: (page: number) => void;
}

/**
 * Der Ort, an dem die Leiste hängt — gesetzt von `ToolbarRegion`, gelesen von
 * `Toolbar`.
 *
 * `null` heißt: Es gibt keine Inhaltsfläche über dieser Ansicht. Dann zeichnet
 * die Leiste nichts, statt sich mitten in den Inhalt zu setzen.
 */
const Region = createContext<HTMLElement | null>(null);

/**
 * Die Inhaltsfläche, in der die Leiste hängt: ein leeres erstes Flex-Kind und
 * dahinter, was die Fläche sonst trägt.
 *
 * Das Gerüst setzt sie in die Inhaltsfläche (`Shell`). In einem Test einer
 * einzelnen Ansicht steht sie um die Ansicht — dieselbe Klammer, damit die
 * Leiste dort dort erscheint, wo sie auch im Betrieb erscheint.
 */
export function ToolbarRegion({ children }: { children: ReactNode }): ReactElement {
  // Ein Zustand und keine `ref`: Erst wenn der Knoten steht, darf das Portal
  // hineinzeichnen, und nur ein Zustand löst dafür ein zweites Zeichnen aus.
  const [host, setHost] = useState<HTMLDivElement | null>(null);

  return (
    <>
      {/* `display: contents` und keine eigene Fläche: Die Leiste soll **selbst**
          das erste Flex-Kind der Inhaltsfläche sein und nicht in einem Kasten
          darin stehen. Ohne Leiste ist dieser Knoten nichts — kein leeres Band,
          keine Höhe, keine Linie. */}
      <div ref={setHost} style={{ display: "contents" }} />
      <Region.Provider value={host}>{children}</Region.Provider>
    </>
  );
}

/**
 * Die Leiste selbst. `children` sind die Knöpfe des Screens, `paging` sein
 * Seitennavigator.
 *
 * Die Maße sind die der Vorlage, unverändert: `gap: 10`, `padding: 0 20px`
 * (`reference/AppShell.jsx` Zeile 189). Für 10 und 20 führt das System kein
 * Token — `--sp-10` ist 24px —, und der Rahmen schreibt sie selbst als Zahl; das
 * Maßpaar hier aufzuteilen, halb Token und halb Zahl, machte aus einem
 * abgelesenen Paar zwei Werte mit verschiedener Herkunft.
 */
export function Toolbar({
  children,
  paging = null,
}: {
  children?: ReactNode;
  paging?: ToolbarPaging | null;
}): ReactElement | null {
  const host = useContext(Region);

  if (host === null) {
    return null;
  }

  return createPortal(
    <div
      // Eine Werkzeugleiste ist eine Werkzeugleiste; der Vorleser sagt sie als
      // solche an und nicht als weiteren Kasten.
      role="toolbar"
      aria-label="Aktionen"
      // Die Klasse des Design-Systems für ein Band, das waagerecht überläuft:
      // Sie nimmt den Rollbalken weg, ohne das Scrollen wegzunehmen.
      className="tabstrip"
      style={{
        // Über der scrollenden Fläche: Was aus dieser Leiste aufklappt, klappt
        // über den Inhalt und nicht darunter.
        position: "relative",
        zIndex: 30,
        height: "var(--h-toolbar)",
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 10,
        paddingLeft: 20,
        paddingRight: 20,
        borderBottom: "1px solid var(--line-f)",
        background: "var(--bg-band)",
        backdropFilter: "var(--blur-band)",
        WebkitBackdropFilter: "var(--blur-band)",
        borderTopLeftRadius: "var(--r-surface)",
        overflowX: "auto",
      }}
    >
      {children}

      {/* Der rechte Rand. Er steht auch leer da, weil `margin-left: auto` die
          Knöpfe links hält — mit oder ohne Navigator dahinter. */}
      <div style={{ marginLeft: "auto", flex: "none", display: "flex", alignItems: "center", gap: 4 }}>
        <PageTurner paging={paging} />
      </div>
    </div>,
    host,
  );
}

/**
 * Der Seitennavigator am rechten Rand — oder nichts.
 *
 * **Bei einer einzigen Seite bleibt der Bereich stehen und die Schalter fallen
 * weg**: „1–2 von 2" ist eine Auskunft, vier tote Schalter sind keine. Dieselbe
 * Entscheidung trägt der Navigator im Inhalt (`views/list.tsx`), und sie ist
 * dieselbe, weil es dieselbe Frage ist.
 */
function PageTurner({ paging }: { paging: ToolbarPaging | null }): ReactElement | null {
  if (paging === null) {
    return null;
  }

  return (
    <>
      {/* Der Trenner der Vorlage: eine Haarlinie über 22 Punkte in `--line-d`.
          Er trennt die Knöpfe des Screens vom Blättern. */}
      <span
        aria-hidden="true"
        style={{ flex: "none", width: 1, height: 22, background: "var(--line-d)", marginRight: 4 }}
      />

      {paging.pages <= 1 ? (
        <span
          style={{
            fontSize: "var(--fs-12)",
            color: "var(--fg-8)",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {paging.rangeLabel}
        </span>
      ) : (
        <Pagination
          page={paging.page}
          pages={paging.pages}
          rangeLabel={paging.rangeLabel}
          onChange={paging.onTurn}
        />
      )}
    </>
  );
}
